import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  FindBuyOrderListQuery,
  FindCustomerForExportOrderBody,
  FindCustomerForExportOrderQuery,
  FindDraftOrdersForPartnerBody,
  FindDraftOrdersForPartnerQuery,
  FindFirstOrderForMultiConfirmUpdateStatusBody,
  FindOrderItemQuantityStatisticsQuery,
  FindOrderLogsWithEntityQuery,
  FindOrdersToBalanceOptions,
  FindOrdersUpdateMultiQuery,
  FindPaymentTransactionCustomBody,
  FindProcessingOrderUpdateStatusQuery,
  FindRevenueForEntityProductBody,
  FindRevenueForEntityProductQuery,
  FindSaleOrderListQuery,
  FindStatisticTotalInfoForChainEntityQuery,
  FindStatisticTotalInfoForCustomerQuery,
  FindStatisticTotalInfoForEntityProductQuery,
  FindStatisticTotalInfoForEntityProductWithCustomerQuery,
  OrderApi,
  OrderLogApi,
  ResetTotalQuantityBody,
  UpdateIsSplitLockOrder,
  UpdateMultiOrderStatusForSplitOrderBody,
} from 'api';
import {
  FindChildOrderByCustomerOptions,
  FindOrderDeliveryInfoOptions,
  FindOrderTaskInfoOptions,
} from 'api/common';
import { CONNECT_ROLE, ENTITY_TYPE } from 'utils';

export const formatCustomerItemData = item => {
  let orderName = '';
  const deletedOrderLogs = item?.orderLogs.filter(
    orderItem => orderItem.action === 'DELETE_PRODUCT'
  );
  const isExistDeletedOrderItems = deletedOrderLogs?.length > 0;
  const deletedOrderItems: any[] = [];
  if (isExistDeletedOrderItems) {
    const deletedProductsOrderItems = item?.orderLogs
      .filter(orderItem => orderItem.action === 'DELETE_PRODUCT')
      .map(item => item.additionalInfo.deleteProducts);
    deletedProductsOrderItems?.forEach((orderItem: any) => {
      orderItem.forEach(deleteProductLog => {
        deletedOrderItems.push({
          ...deleteProductLog,
          product: {
            id: deleteProductLog.productID,
            name: deleteProductLog.productName,
            code: deleteProductLog.productSKU,
            avatar: deleteProductLog.productAvatar,
          },
          customerProduct: {
            id: deleteProductLog.customerProductID,
            name: deleteProductLog.customerProductName,
            code: deleteProductLog.customerProductSKU,
            avatar: deleteProductLog.customerProductAvatar,
          },
        });
        if (orderName === '') {
          orderName = deleteProductLog.productName;
        } else {
          orderName = `${orderName}-${deleteProductLog.productName}`;
        }
      });
    });
  } else {
    item?.orderItems?.forEach((orderItem: any) => {
      if (orderName === '') {
        orderName = orderItem.product.name;
      } else {
        orderName = `${orderName}-${orderItem.product.name}`;
      }
    });
  }

  return {
    ...item,
    key: item.id,
    orderName,
    priceBox: {
      totalPrice: item.totalPrice,
      createdAt: item.updateAt,
    },
    infoBox: {
      id: item.id,
      customerName: item.customer.displayName,
      type: item.type,
    },
    optionBox: {
      id: item.id,
      code: item.code,
      orderOptions: item.orderOptions,
    },
    isExistDeletedOrderItems,
    deletedOrderItems,
  };
};

export const formatSupplierItemData = item => {
  let orderName = '';
  const deletedOrderLogs = item?.orderLogs.filter(
    orderItem => orderItem.action === 'DELETE_PRODUCT'
  );
  const isExistDeletedOrderItems = deletedOrderLogs?.length > 0;
  const deletedOrderItems: any[] = [];
  if (isExistDeletedOrderItems) {
    const deletedProductsOrderItems = item?.orderLogs
      .filter(orderItem => orderItem.action === 'DELETE_PRODUCT')
      .map(item => item.additionalInfo.deleteProducts);
    deletedProductsOrderItems?.forEach((orderItem: any) => {
      orderItem.forEach(deleteProductLog => {
        deletedOrderItems.push({
          ...deleteProductLog,
          product: {
            id: deleteProductLog.productID,
            name: deleteProductLog.productName,
            code: deleteProductLog.productSKU,
            avatar: deleteProductLog.productAvatar,
          },
          customerProduct: {
            id: deleteProductLog.customerProductID,
            name: deleteProductLog.customerProductName,
            code: deleteProductLog.customerProductSKU,
            avatar: deleteProductLog.customerProductAvatar,
          },
        });
        if (orderName === '') {
          orderName = deleteProductLog.productName;
        } else {
          orderName = `${orderName}-${deleteProductLog.productName}`;
        }
      });
    });
  } else {
    item?.orderItems?.forEach((orderItem: any) => {
      if (orderName === '') {
        orderName = orderItem.product.name;
      } else {
        orderName = `${orderName}-${orderItem.product.name}`;
      }
    });
  }
  return {
    ...item,
    key: item.id,
    orderName,
    priceBox: {
      totalPrice: item.totalPrice,
      createdAt: item.updateAt,
    },
    infoBox: {
      id: item.id,
      supplierName: item.supplier.displayName,
    },
    optionBox: {
      id: item.id,
      code: item.code,
      orderOptions: item.orderOptions,
    },
  };
};

const formatSupplierData = (data: any[]) =>
  data.map((item: any) => formatSupplierItemData(item));

export const updateOrderAddress = createAsyncThunk<
  any,
  { id: string; body: any },
  { rejectValue: { message: string } }
>('order/updateOrderAddress', async ({ id, body }, { rejectWithValue }) => {
  try {
    const response = await OrderApi.updateOrderAddress(id, body);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findOrdersAsCustomer = createAsyncThunk(
  'order/findOrderAsCustomer',
  async (data: { entityType: number; option: any }, { rejectWithValue }) => {
    try {
      const { entityType, option } = data;
      let response: any;
      if (entityType === ENTITY_TYPE.COLLABORATOR) {
        response = await OrderApi.findByCollaborator(option);
      } else {
        response = await OrderApi.findByAdminAsCustomer(option);
      }
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const findOrdersAsSupplier = createAsyncThunk(
  'order/findOrderAsSupplier',
  async (data: { entityType: number; option: any }, { rejectWithValue }) => {
    try {
      const { entityType, option } = data;
      let response: any;
      if (entityType === ENTITY_TYPE.COLLABORATOR) {
        response = await OrderApi.findByCollaborator(option);
      } else {
        response = await OrderApi.findByAdminAsSupplier(option);
      }
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const findBuyOrderList = createAsyncThunk<
  any,
  FindBuyOrderListQuery,
  { rejectValue: { message: string } }
>('order/findBuyOrderList', async (option, { rejectWithValue }) => {
  try {
    let response: any;

    if (option.entityType === ENTITY_TYPE.COLLABORATOR) {
      response = await OrderApi.findSaleListByCollaborator(option);
    } else {
      response = await OrderApi.findBuyListByAdmin(option);
    }

    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findSaleOrderList = createAsyncThunk<
  any,
  FindSaleOrderListQuery,
  { rejectValue: { message: string } }
>('order/findSaleOrderList', async (option, { rejectWithValue }) => {
  try {
    let response: any;

    if (option.entityType === ENTITY_TYPE.COLLABORATOR) {
      response = await OrderApi.findSaleListByCollaborator(option);
    } else {
      response = await OrderApi.findSaleListByAdmin(option);
    }

    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findOrderForSplitOrder = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>('order/findOrderForSplitOrder', async (id: string, { rejectWithValue }) => {
  try {
    const response = await OrderApi.findOrderForSplitOrder(id);

    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findOrdersToMultiUpdateStatus = createAsyncThunk<
  any,
  FindOrdersUpdateMultiQuery,
  { rejectValue: { message: string } }
>(
  'order/findOrdersToMultiUpdateStatus',
  async (option, { rejectWithValue }) => {
    try {
      const response = await OrderApi.findOrdersToMultiUpdateStatus(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findSaleOrderListStatistics = createAsyncThunk<
  any,
  FindSaleOrderListQuery,
  { rejectValue: { message: string } }
>('order/findSaleOrderListStatistics', async (option, { rejectWithValue }) => {
  try {
    const response = await OrderApi.findSaleListStatistics(option);

    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findForBalanceList = createAsyncThunk(
  'order/findForBalanceList',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.findByAdminAsSupplier(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOneOrderByOrderCode = createAsyncThunk<
  any,
  { orderCode: string },
  { rejectValue: { message: string } }
>(
  'order/findOneOrderByOrderCode',
  async ({ orderCode }, { rejectWithValue }) => {
    try {
      const response = await OrderApi.findOneByOrderCode(orderCode);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOrderToExportWithProductInfo = createAsyncThunk(
  'order/findOrderToExportWithProductInfo',
  async (params: { option; body }, { rejectWithValue }) => {
    try {
      const { body, option } = params;
      const response = await OrderApi.findOrderToExportWithProductInfo(
        option,
        body
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOrderToExportWithQuantityInfo = createAsyncThunk(
  'order/findOrderToExportWithQuantityInfo',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.findOrderToExportWithQuantityInfo(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findChildOrderByCustomer = createAsyncThunk<
  any,
  { params: FindChildOrderByCustomerOptions },
  { rejectValue: { message: string } }
>('order/findOrderByCustomer', async ({ params }, { rejectWithValue }) => {
  try {
    const response = await OrderApi.findChildOrdersByCustomer(params);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findOrderDeliveryInfo = createAsyncThunk<
  any,
  { body: FindOrderDeliveryInfoOptions },
  { rejectValue: { message: string } }
>('order/findOrderDeliveryInfo', async ({ body }, { rejectWithValue }) => {
  try {
    const response = await OrderApi.findOrderDeliveryInfo(body);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findOrderTaskInfo = createAsyncThunk<
  any,
  { body: FindOrderTaskInfoOptions },
  { rejectValue: { message: string } }
>('order/findOrderTaskInfo', async ({ body }, { rejectWithValue }) => {
  try {
    const response = await OrderApi.findOrderTaskInfo(body);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findPurChaseOrderTaskInfo = createAsyncThunk<
  any,
  { body: FindOrderTaskInfoOptions },
  { rejectValue: { message: string } }
>('order/findPurChaseOrderTaskInfo', async ({ body }, { rejectWithValue }) => {
  try {
    const response = await OrderApi.findPurchaseOrderTaskInfo(body);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const quickMergeWholesaleOrders = createAsyncThunk<
  any,
  { body: any },
  { rejectValue: { message: string } }
>('order/findOrderTaskInfo', async ({ body }, { rejectWithValue }) => {
  try {
    if (body?.isOptimize) {
      const responseV2 = await OrderApi.quickMergeWholesaleOrdersV2(body);
      return responseV2;
    }
    const response = await OrderApi.quickMergeWholesaleOrders(body);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findForCollaboratorPendingList = createAsyncThunk(
  'order/findForCollaboratorPendingList',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.findByCollaborator(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOneOrder = createAsyncThunk(
  'order/findOneOrder',
  async (params: FinOneOrderParams, { rejectWithValue }) => {
    try {
      let response: any;
      const { id, option, entityType, role } = params;
      if (role === CONNECT_ROLE.CUSTOMER) {
        response = await OrderApi.findOneAsCustomerByAdmin(id, option);
      } else if (entityType === ENTITY_TYPE.COLLABORATOR) {
        response = await OrderApi.findOneByCollaborator(id, option);
      } else {
        response = await OrderApi.findOneBySupplier(id, option);
      }

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOneOrderBill = createAsyncThunk(
  'order/findOneOrderBill',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await OrderApi.findOneOrderBill(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOrderBills = createAsyncThunk(
  'order/findOrderBills',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.findOrderBills(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOneAdditionalOrder = createAsyncThunk(
  'order/findOneAdditionalOrder',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, option } = params;
      const response = await OrderApi.findOneAdditionalOrder(id, option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOrderLogsWithEntity = createAsyncThunk(
  'order/findOrderLogsWithEntity',
  async (
    {
      orderID,
      query,
    }: { orderID: string; query: FindOrderLogsWithEntityQuery },
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderLogApi.findWithEntity(orderID, query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createMultiOrdersWithPaths = createAsyncThunk(
  'order/createMultiOrdersWithPaths',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.createMultiOrdersWithPaths(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const confirmMultiOrdersWithPaths = createAsyncThunk(
  'order/confirmMultiOrdersWithPaths',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.confirmMultiOrdersWithPaths(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const confirmMultiOrdersWithPath = createAsyncThunk(
  'order/confirmMultiOrdersWithPath',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.confirmMultiOrdersWithPath(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createOrder = createAsyncThunk(
  'order/createOrder',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.create(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const confirmOrderDelivered = createAsyncThunk(
  'order/confirmOrderDelivered',
  async (parmas: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.confirmOrderDelivered(parmas);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const confirmOrderReceived = createAsyncThunk(
  'order/confirmOrderReceived',
  async (parmas: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.confirmOrderReceived(parmas);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createForward = createAsyncThunk(
  'order/createForward',
  async (param: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.createForward(param);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrder = createAsyncThunk(
  'order/updateOrder',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await OrderApi.update(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrderNote = createAsyncThunk(
  'order/updateOrderNote',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await OrderApi.updateOrderNote(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkCanUpdateOrderNote = createAsyncThunk(
  'order/checkCanUpdateOrderNote',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await OrderApi.checkCanUpdateOrderNote(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  'order/updateOrderStatus',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await OrderApi.updateStatus(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrderPaymentStatus = createAsyncThunk(
  'order/updateOrderPaymentStatus',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await OrderApi.updatePaymentStatus(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const mergeAdditionalOrder = createAsyncThunk(
  'order/mergeAdditionalOrder',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id } = params;
      const response = await OrderApi.mergeAdditionalOrder(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const manualUpdatePendingExecuteOrderStatus = createAsyncThunk(
  'order/manualUpdatePendingExecuteOrderStatus',
  async (params: { id: string }, { rejectWithValue }) => {
    try {
      const { id } = params;
      const response = await OrderApi.manualUpdatePendingExecuteOrderStatus(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const manualConfirmPendingExecuteOrderStatus = createAsyncThunk(
  'order/manualConfirmPendingExecuteOrderStatus',
  async (params: { id: string }, { rejectWithValue }) => {
    try {
      const { id } = params;
      const response = await OrderApi.manualConfirmPendingExecuteOrderStatus(
        id
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateMultiStatusOrder = createAsyncThunk(
  'order/updateMultiStatusOrder',
  async (
    body: { orders: any[]; shouldCheckAllItemSupplierQuantityZero: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderApi.updateMultiStatus(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const resetTotalQuantity = createAsyncThunk(
  'order/resetTotalQuantity',
  async (body: ResetTotalQuantityBody, { rejectWithValue }) => {
    try {
      const response = await OrderApi.resetTotalQuantity(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const mergeOrder = createAsyncThunk(
  'order/mergeOrder',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await OrderApi.mergeOrder(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

// get EntityDeliveryFee by supplierID
export const updateIsSplitLockOrder = createAsyncThunk(
  'order/updateIsSplitLockOrder',
  async (
    param: { id: string; body: UpdateIsSplitLockOrder },
    { rejectWithValue }
  ) => {
    try {
      const { id, body } = param;
      const response = await OrderApi.updateIsSplitLock(id, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOrdersToBalance = createAsyncThunk(
  'order/findOrdersToBalance',
  async (params: FindOrdersToBalanceOptions, { rejectWithValue }) => {
    try {
      const response = await OrderApi.findToBalance(params);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateMultiOrderStatusForSplitOrder = createAsyncThunk(
  'order/updateMultiOrderStatusForSplitOrder',
  async (
    body: UpdateMultiOrderStatusForSplitOrderBody,
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderApi.updateMultiOrderStatusForSplitOrder(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findFirstOrderForMultiConfirmUpdateStatus = createAsyncThunk(
  'order/findFirstOrderForMultiConfirmUpdateStatus',
  async (
    {
      options,
      body,
    }: {
      options: any;
      body: FindFirstOrderForMultiConfirmUpdateStatusBody;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderApi.findFirstOrderForMultiConfirmUpdateStatus(
        options,
        body
      );
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOrderForConfirmUpdateStatus = createAsyncThunk(
  'order/findOrderForConfirmUpdateStatus',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await OrderApi.findOrderForConfirmUpdateStatus(id);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateSupplierQuantityEqualCustomerQuantity = createAsyncThunk(
  'order/updateSupplierQuantityEqualCustomerQuantity',
  async (id: string, { rejectWithValue }) => {
    try {
      const response =
        await OrderApi.updateSupplierQuantityEqualCustomerQuantity(id);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findStatisticTotalInfoForEntityProduct = createAsyncThunk(
  'order/findStatisticTotalInfoForEntityProduct',
  async (
    param: FindStatisticTotalInfoForEntityProductQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderApi.findStatisticTotalInfoForEntityProduct(
        param
      );
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findStatisticTotalInfoForChainEntity = createAsyncThunk(
  'order/findStatisticTotalInfoForChainEntity',
  async (
    param: FindStatisticTotalInfoForChainEntityQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderApi.findStatisticTotalInfoForChainEntity(
        param
      );
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findRevenueForEntityProduct = createAsyncThunk(
  'order/findRevenueForEntityProduct',
  async (
    param: {
      body: FindRevenueForEntityProductBody;
      query: FindRevenueForEntityProductQuery;
    },
    { rejectWithValue }
  ) => {
    try {
      const { body, query } = param;
      const response = await OrderApi.findRevenueForEntityProduct(query, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findStatisticTotalInfoForCustomer = createAsyncThunk(
  'order/findStatisticTotalInfoForCustomer',
  async (
    param: FindStatisticTotalInfoForCustomerQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderApi.findStatisticTotalInfoForCustomerEntity(
        param
      );
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findStatisticsTotalInfoForEntityProductWithSupplier =
  createAsyncThunk(
    'order/findStatisticsTotalInfoForEntityProductWithSupplier',
    async (
      param: FindStatisticTotalInfoForEntityProductQuery,
      { rejectWithValue }
    ) => {
      try {
        const response =
          await OrderApi.findStatisticsTotalInfoForEntityProductWithSupplier(
            param
          );
        return response;
      } catch (error: any) {
        if (!error.response) {
          throw error;
        }

        return rejectWithValue(error.response.data);
      }
    }
  );

export const findStatisticsTotalInfoForEntityProductWithCustomer =
  createAsyncThunk(
    'order/findStatisticsTotalInfoForEntityProductWithCustomer',
    async (
      param: FindStatisticTotalInfoForEntityProductWithCustomerQuery,
      { rejectWithValue }
    ) => {
      try {
        const response =
          await OrderApi.findStatisticsTotalInfoForEntityProductWithCustomer(
            param
          );
        return response;
      } catch (error: any) {
        if (!error.response) {
          throw error;
        }

        return rejectWithValue(error.response.data);
      }
    }
  );

export const findOrderItemQuantityStatistics = createAsyncThunk(
  'order/findOrderItemQuantityStatistics',
  async (param: FindOrderItemQuantityStatisticsQuery, { rejectWithValue }) => {
    try {
      const response = await OrderApi.findOrderItemQuantityStatistics(param);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findCustomerForExportOrder = createAsyncThunk(
  'order/findCustomerForExportOrder',
  async (
    param: {
      query: FindCustomerForExportOrderQuery;
      body: FindCustomerForExportOrderBody;
    },
    { rejectWithValue }
  ) => {
    try {
      const { query, body } = param;
      const response = await OrderApi.findCustomerForExportOrder(query, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPaymentTransactionWithOrder = createAsyncThunk(
  'order/findPaymentTransactionWithOrder',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await OrderApi.findPaymentTransaction(id);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPaymentTransactionCustom = createAsyncThunk(
  'order/findPaymentTransactionCustom',
  async (body: FindPaymentTransactionCustomBody, { rejectWithValue }) => {
    try {
      const response = await OrderApi.findPaymentTransactionCustom(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPaymentTransactionWithBill = createAsyncThunk(
  'order/findPaymentTransactionWithBill',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await OrderApi.findPaymentBillTransaction(id);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findDraftOrdersForPartner = createAsyncThunk(
  'order/findDraftOrdersForPartner',
  async (
    params: {
      query: FindDraftOrdersForPartnerQuery;
      body: FindDraftOrdersForPartnerBody;
    },
    { rejectWithValue }
  ) => {
    try {
      const { query, body } = params;
      const response = await OrderApi.findDraftOrdersForPartner(query, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProcessingOrderUpdateStatusForSplitOrder = createAsyncThunk(
  'order/findProcessingOrderUpdateStatusForSplitOrder',
  async (option: FindProcessingOrderUpdateStatusQuery, { rejectWithValue }) => {
    try {
      const response =
        await OrderApi.findProcessingOrderUpdateStatusForSplitOrder(option);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface State {
  reloadFlag: boolean;
  reloadRevenueEntityProductFlag: boolean;
  item: any;
  list: Array<any>;
  balanceList: Array<any>;

  supplierTabList: Array<any>;
  customerTabList: Array<any>;
  collaboratorTabList: Array<any>;

  customerPendingList: Array<any>;
  customerConfirmList: Array<any>;
  customerProcessingList: Array<any>;
  customerCompleteList: Array<any>;
  customerDraftList: Array<any>;
  customerDeliveryList: Array<any>;
  customerRejectList: Array<any>;

  supplierPendingList: Array<any>;
  supplierConfirmList: Array<any>;
  supplierProcessingList: Array<any>;
  supplierCompleteList: Array<any>;
  supplierDeliveryList: Array<any>;
  supplierRejectList: Array<any>;

  collaboratorPendingList: Array<any>;
  collaboratorConfirmList: Array<any>;
  collaboratorProcessingList: Array<any>;
  collaboratorCompleteList: Array<any>;
  collaboratorDeliveryList: Array<any>;
  collaboratorRejectList: Array<any>;
  temporaryOrder: any;
  tempCustomerNotesWholeSale: any;
  tempDeliveryOptionsGroupBySupplier: any;
  paymentMethods: any;
  entityDeliveryFees: any;
  entityLocationExists: any;
  savedPagination: any;
  savedList: Array<any>;
}

const EMPTY_TEMP_ORDER = {
  deliveryFee: 0,
  totalPrice: 0,
  totalItem: 0,
  totalAdjustment: 0,
  adjustments: [],
  orders: {},
};

const initialState: State = {
  reloadFlag: false,
  reloadRevenueEntityProductFlag: false,
  item: {},
  list: [],
  balanceList: [],

  supplierTabList: [],
  customerTabList: [],
  collaboratorTabList: [],

  customerPendingList: [],
  customerConfirmList: [],
  customerProcessingList: [],
  customerCompleteList: [],
  customerDraftList: [],
  customerDeliveryList: [],
  customerRejectList: [],

  supplierPendingList: [],
  supplierConfirmList: [],
  supplierProcessingList: [],
  supplierCompleteList: [],
  supplierDeliveryList: [],
  supplierRejectList: [],

  collaboratorPendingList: [],
  collaboratorConfirmList: [],
  collaboratorProcessingList: [],
  collaboratorCompleteList: [],
  collaboratorDeliveryList: [],
  collaboratorRejectList: [],
  temporaryOrder: EMPTY_TEMP_ORDER,
  tempCustomerNotesWholeSale: {},
  tempDeliveryOptionsGroupBySupplier: {},
  paymentMethods: {},
  entityDeliveryFees: {},
  entityLocationExists: {},
  savedPagination: { current: 1, next: 2, prev: 0, take: 10, total: 0 },
  savedList: [],
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    reloadOrder(state) {
      state.reloadFlag = !state.reloadFlag;
    },
    reloadRevenueEntityProduct(state) {
      state.reloadRevenueEntityProductFlag =
        !state.reloadRevenueEntityProductFlag;
    },
    resetTempOrder(state) {
      state.temporaryOrder = EMPTY_TEMP_ORDER;
    },
    updateTempCustomerNote(state, action) {
      const { supplierID, customerNote } = action.payload;

      if (state.tempCustomerNotesWholeSale[supplierID]) {
        state.tempCustomerNotesWholeSale[supplierID].customerNote =
          customerNote;
      } else {
        state.tempCustomerNotesWholeSale[supplierID] = { customerNote };
      }
    },
    resetTempSupplierNoteBySupplierIDs(state, action) {
      const newData = { ...state.tempCustomerNotesWholeSale };
      const supplierIDs = action.payload;
      supplierIDs.map(supplierID => {
        if (newData[supplierID]) {
          newData[supplierID].customerNote = '';
        }
        return null;
      });
      state.tempCustomerNotesWholeSale = newData;
    },
    updateTempDeliveryOptionsGroupBySupplier(state, action) {
      const data = { ...state.tempDeliveryOptionsGroupBySupplier };

      const { supplierID, deliveryMethod } = action.payload;

      if (data[supplierID]) {
        data[supplierID].deliveryMethod = deliveryMethod;
      } else {
        data[supplierID] = { deliveryMethod };
      }
      state.tempDeliveryOptionsGroupBySupplier = data;
    },
    updatePaymentMethodBySupplier(state, action) {
      const { supplierID, paymentMethod } = action.payload;
      state.paymentMethods[supplierID] = paymentMethod;
    },
    updateSavedPaginationOrder(state, action) {
      state.savedPagination = action.payload;
    },
    updateSavedListOrder(state, action) {
      state.savedList = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(findOrdersAsCustomer.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.list = data;
      })
      .addCase(findOrdersAsSupplier.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.list = data;
      })
      .addCase(findForBalanceList.fulfilled, (state, action) => {
        const { data } = action.payload;
        const newData = data.map((item: any) => {
          return {
            ...item,
            key: item.id,
            infoBox: {
              id: item.id,
              customerName: item.customer.displayName,
              status: item.status,
            },
            priceBox: {
              totalPrice: item.totalPrice,
              createdAt: item.createdAt,
            },
            optionBox: {
              id: item.id,
              code: item.code,
              orderOptions: item.orderOptions,
            },
          };
        });

        state.balanceList = newData;
      })
      .addCase(findForCollaboratorPendingList.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.collaboratorPendingList = formatSupplierData(data);
      })
      .addCase(findOneOrder.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.item = data;
      });
  },
});

const { actions, reducer: orderReducer } = orderSlice;
export const {
  reloadOrder,
  reloadRevenueEntityProduct,
  resetTempOrder,
  updateTempCustomerNote,
  resetTempSupplierNoteBySupplierIDs,
  updateTempDeliveryOptionsGroupBySupplier,
  updatePaymentMethodBySupplier,
  updateSavedPaginationOrder,
  updateSavedListOrder,
} = actions;

export default orderReducer;

export interface FinOneOrderParams {
  id: string;
  entityType: number;
  role: number;
  option: any;
}
